import { decodeJsonObject, postLocalJson } from "./localJsonTransport";
import type { CreateThreadInput } from "@cleo/client-runtime/operations";
import type { EnvironmentId } from "@cleo/contracts";

// JB1 — the browser side of the durable birth-attempt intent. The gesture
// records the prepared identity pair once through the loopback door, and
// resume queries the recorded intents at startup. Settlement is computed by
// the caller against the authoritative thread stream (executionArrived) —
// these rows carry intent, never status.

export interface WorldSessionBirthAttemptRecord {
  readonly worldKey: string;
  readonly sessionId: string;
  readonly environmentId: EnvironmentId;
  readonly input: CreateThreadInput;
}

export interface StoredWorldSessionBirthAttempt {
  readonly worldKey: string;
  readonly sessionId: string;
  readonly environmentId: string;
  readonly input: CreateThreadInput;
  readonly recordedAt: string;
}

export async function recordWorldSessionBirthAttempt(
  attempt: WorldSessionBirthAttemptRecord,
  fetchImpl: typeof fetch = fetch,
): Promise<{ readonly ok: boolean }> {
  try {
    const response = await postLocalJson(
      "/api/neucleos/world/sessions/attempts/record",
      attempt,
      fetchImpl,
    );
    const body = await decodeJsonObject(response);
    return { ok: response.ok && body?.["ok"] === true };
  } catch {
    return { ok: false };
  }
}

function validStoredAttempt(value: unknown): value is StoredWorldSessionBirthAttempt {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  const input = row["input"];
  return (
    typeof row["worldKey"] === "string" &&
    typeof row["sessionId"] === "string" &&
    typeof row["environmentId"] === "string" &&
    typeof row["recordedAt"] === "string" &&
    typeof input === "object" &&
    input !== null &&
    !Array.isArray(input) &&
    typeof (input as Record<string, unknown>)["threadId"] === "string"
  );
}

/**
 * Read the recorded attempts for one World (optionally one Session), newest
 * last. Absence is an empty list. An unreadable/refusing door is a failure:
 * callers must not confuse "could not establish whether an identity exists"
 * with "no identity exists" and mint a competing execution.
 */
export async function readWorldSessionBirthAttempts(
  input: { readonly worldKey: string; readonly sessionId?: string },
  fetchImpl: typeof fetch = fetch,
): Promise<ReadonlyArray<StoredWorldSessionBirthAttempt>> {
  const response = await postLocalJson("/api/neucleos/world/sessions/attempts", input, fetchImpl);
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true || !Array.isArray(body["attempts"])) {
    throw new Error("world-session-birth-attempts-unavailable");
  }
  return body["attempts"].filter(validStoredAttempt);
}
